import { useEffect, useRef, useState } from "react";
import { useDict } from "@/i18n/useDict";

const statTargets = [300000, 100000, 24];

const Counter = ({ target, started, locale }: { target: number; started: boolean; locale: string }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!started) return;
    const duration = 2000;
    const steps = 60;
    const inc = target / steps;
    let current = 0;
    const interval = setInterval(() => {
      current += inc;
      if (current >= target) {
        setVal(target);
        clearInterval(interval);
      } else {
        setVal(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(interval);
  }, [started, target]);
  return <span>{val.toLocaleString(locale)}</span>;
};

const CaseStudySection = () => {
  const { dict, lang } = useDict();
  const ref = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);
  const stats = dict.caseStudy.stats.map((s, i) => ({ ...s, target: statTargets[i] }));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            setStarted(true);
          }
        });
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="studiu" className="py-20 md:py-32 px-6 bg-gradient-section">
      <div ref={ref} className="max-w-5xl mx-auto animate-on-scroll">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">{dict.caseStudy.heading}</h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            {dict.caseStudy.subheading}
          </p>
        </div>
        <p className="text-center text-muted-foreground max-w-3xl mx-auto mb-12 leading-relaxed">
          {dict.caseStudy.body}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
          {stats.map((s, i) => (
            <div key={i} className="rounded-2xl border border-border bg-card p-8 text-center" style={{ boxShadow: "var(--shadow-card)" }}>
              <div className="text-4xl font-extrabold text-primary">
                <Counter target={s.target} started={started} locale={lang} />
              </div>
              <div className="mt-2 text-sm text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
        <blockquote className="relative rounded-2xl bg-card border border-border p-8 text-center" style={{ boxShadow: "var(--shadow-card)" }}>
          <svg className="mx-auto mb-4 text-primary/30" width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M11.3 2.5c-.6.3-1 .7-1.3 1.2-.3.5-.5 1.2-.5 2 0 1.1.3 2 .8 2.7.6.7 1.3 1 2.2 1-.1.7-.4 1.4-.9 2-.5.7-1.1 1.2-1.8 1.6l.6 1.2c1.2-.6 2.1-1.4 2.8-2.5.7-1.1 1-2.3 1-3.7 0-1.5-.4-2.7-1.2-3.5-.7-.9-1.7-1.3-2.8-1.3l.1.3zm-7 0c-.6.3-1 .7-1.3 1.2-.3.5-.5 1.2-.5 2 0 1.1.3 2 .8 2.7.6.7 1.3 1 2.2 1-.1.7-.4 1.4-.9 2-.5.7-1.1 1.2-1.8 1.6l.6 1.2c1.2-.6 2.1-1.4 2.8-2.5.7-1.1 1-2.3 1-3.7 0-1.5-.4-2.7-1.2-3.5C5.3 2.6 4.3 2.2 3.2 2.2l.1.3z"/></svg>
          <p className="text-foreground text-lg italic leading-relaxed">
            „{dict.caseStudy.quote}"
          </p>
        </blockquote>
      </div>
    </section>
  );
};

export default CaseStudySection;
